import React, { useState, useEffect } from 'react';
import { 
  Tag, Plus, Search, Edit2, Trash2, Users, Building2, 
  Calendar, Percent, DollarSign, Gift, Check, X, 
  ChevronDown, ChevronUp, Loader2, AlertTriangle, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PROMOTION_TYPES = [
  { value: 'PERCENT_DISCOUNT', label: 'Percentage Discount', icon: Percent },
  { value: 'FIXED_AMOUNT_DISCOUNT', label: 'Fixed Amount Discount', icon: DollarSign },
  { value: 'FREE_SEATS', label: 'Free Seats', icon: Users },
  { value: 'FREE_SUBSCRIPTION', label: 'Free Subscription (100% off)', icon: Gift },
];

const PROMOTION_SCOPES = [
  { value: 'TENANT_SUBSCRIPTION', label: 'Tenant Subscription' },
  { value: 'TENANT_ALL_SEATS', label: 'All Seats' },
  { value: 'TENANT_SPECIFIC_SEATS', label: 'Specific Seats' },
];

const STATUS_COLORS = {
  ACTIVE: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  INACTIVE: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  EXPIRED: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function PromotionManagement() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [promotionDetails, setPromotionDetails] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'PERCENT_DISCOUNT',
    scope: 'TENANT_SUBSCRIPTION',
    target_plan: '',
    percent_off: '',
    amount_off_cents: '',
    free_seat_count: '',
    start_at: '',
    end_at: '',
    max_redemptions: '',
    max_redemptions_per_tenant: '',
    applies_to_new_tenants_only: false,
    stackable: false,
    priority: 0,
    description: '',
  });
  
  const [assignData, setAssignData] = useState({
    tenant_id: '',
    seat_count_comped: '',
    effective_end_at: '',
    notes: '',
  });
  
  const [tenants, setTenants] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPromotions();
    fetchTenants();
  }, [statusFilter, typeFilter]);

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/api/admin/promotions`;
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromotions(response.data.promotions || []);
    } catch (error) {
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Failed to fetch tenants');
    }
  };

  const fetchPromotionDetails = async (promotionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/promotions/${promotionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromotionDetails(response.data);
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to load promotion details');
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        percent_off: formData.percent_off ? parseFloat(formData.percent_off) : null,
        amount_off_cents: formData.amount_off_cents ? parseInt(formData.amount_off_cents) : null,
        free_seat_count: formData.free_seat_count ? parseInt(formData.free_seat_count) : null,
        max_redemptions: formData.max_redemptions ? parseInt(formData.max_redemptions) : null,
        max_redemptions_per_tenant: formData.max_redemptions_per_tenant ? parseInt(formData.max_redemptions_per_tenant) : null,
        priority: parseInt(formData.priority) || 0,
        start_at: formData.start_at || new Date().toISOString(),
        end_at: formData.end_at || null,
        target_plan: formData.target_plan || null,
        code: formData.code || null,
      };
      
      await axios.post(`${API}/api/admin/promotions`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Promotion created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPromotion) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: formData.name,
        code: formData.code || null,
        status: formData.status,
        end_at: formData.end_at || null,
        max_redemptions: formData.max_redemptions ? parseInt(formData.max_redemptions) : null,
        max_redemptions_per_tenant: formData.max_redemptions_per_tenant ? parseInt(formData.max_redemptions_per_tenant) : null,
        stackable: formData.stackable,
        priority: parseInt(formData.priority) || 0,
        description: formData.description,
      };
      
      await axios.patch(`${API}/api/admin/promotions/${selectedPromotion.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Promotion updated successfully');
      setShowEditDialog(false);
      fetchPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promotionId) => {
    if (!confirm('Are you sure you want to deactivate this promotion?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/promotions/${promotionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promotion deactivated');
      fetchPromotions();
    } catch (error) {
      toast.error('Failed to deactivate promotion');
    }
  };

  const handleAssign = async () => {
    if (!selectedPromotion || !assignData.tenant_id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        promotion_id: selectedPromotion.id,
        tenant_id: assignData.tenant_id,
        seat_count_comped: assignData.seat_count_comped ? parseInt(assignData.seat_count_comped) : null,
        effective_end_at: assignData.effective_end_at || null,
        notes: assignData.notes || null,
      };
      
      await axios.post(`${API}/api/admin/promotions/${selectedPromotion.id}/assign`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Promotion assigned successfully');
      setShowAssignDialog(false);
      setAssignData({ tenant_id: '', seat_count_comped: '', effective_end_at: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeAssignment = async (assignmentId) => {
    if (!confirm('Are you sure you want to revoke this assignment?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/admin/promotion-assignments/${assignmentId}`, 
        { is_revoked: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Assignment revoked');
      if (selectedPromotion) {
        fetchPromotionDetails(selectedPromotion.id);
      }
    } catch (error) {
      toast.error('Failed to revoke assignment');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      type: 'PERCENT_DISCOUNT',
      scope: 'TENANT_SUBSCRIPTION',
      target_plan: '',
      percent_off: '',
      amount_off_cents: '',
      free_seat_count: '',
      start_at: '',
      end_at: '',
      max_redemptions: '',
      max_redemptions_per_tenant: '',
      applies_to_new_tenants_only: false,
      stackable: false,
      priority: 0,
      description: '',
    });
  };

  const openEditDialog = (promotion) => {
    setSelectedPromotion(promotion);
    setFormData({
      ...formData,
      name: promotion.name,
      code: promotion.code || '',
      status: promotion.status,
      end_at: promotion.end_at ? promotion.end_at.split('T')[0] : '',
      max_redemptions: promotion.max_redemptions?.toString() || '',
      max_redemptions_per_tenant: promotion.max_redemptions_per_tenant?.toString() || '',
      stackable: promotion.stackable,
      priority: promotion.priority,
      description: promotion.description || '',
    });
    setShowEditDialog(true);
  };

  const filteredPromotions = promotions.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.code && p.code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getTypeIcon = (type) => {
    const typeConfig = PROMOTION_TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.icon : Tag;
  };

  const formatDiscount = (promo) => {
    switch (promo.type) {
      case 'PERCENT_DISCOUNT':
        return `${promo.percent_off}% off`;
      case 'FIXED_AMOUNT_DISCOUNT':
        return `$${(promo.amount_off_cents / 100).toFixed(2)} off`;
      case 'FREE_SEATS':
        return `${promo.free_seat_count} free seats`;
      case 'FREE_SUBSCRIPTION':
        return '100% off subscription';
      default:
        return 'Discount';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-white">Promotions & Coupons</h1>
          <p className="text-slate-400 mt-1">Manage discounts, comps, and promotional codes</p>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => {
            resetForm();
            setShowCreateDialog(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Promotion
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Types</SelectItem>
                {PROMOTION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Promotions List */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
            </div>
          ) : filteredPromotions.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              No promotions found
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {filteredPromotions.map((promo) => {
                const TypeIcon = getTypeIcon(promo.type);
                return (
                  <div key={promo.id} className="p-4 hover:bg-slate-800/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          promo.status === 'ACTIVE' ? 'bg-blue-500/20' : 'bg-slate-700'
                        }`}>
                          <TypeIcon className={`w-5 h-5 ${
                            promo.status === 'ACTIVE' ? 'text-blue-400' : 'text-slate-400'
                          }`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{promo.name}</span>
                            {promo.code && (
                              <Badge className="bg-slate-700 text-slate-300 font-mono text-xs">
                                {promo.code}
                              </Badge>
                            )}
                            <Badge className={STATUS_COLORS[promo.status]}>
                              {promo.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                            <span>{formatDiscount(promo)}</span>
                            <span>•</span>
                            <span>{PROMOTION_SCOPES.find(s => s.value === promo.scope)?.label}</span>
                            {promo.end_at && (
                              <>
                                <span>•</span>
                                <span>Ends {new Date(promo.end_at).toLocaleDateString()}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-400 mr-4">
                          {promo.total_redemptions || 0} uses
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedPromotion(promo);
                            fetchPromotionDetails(promo.id);
                          }}
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedPromotion(promo);
                            setShowAssignDialog(true);
                          }}
                          disabled={promo.status !== 'ACTIVE'}
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(promo)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {promo.status === 'ACTIVE' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300"
                            onClick={() => handleDelete(promo.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Promotion Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Promotion</DialogTitle>
            <DialogDescription className="text-slate-400">
              Define a new promotional discount, comp, or coupon
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Summer 50% Off"
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Coupon Code (optional)</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., SUMMER50"
                  className="mt-1 bg-slate-800 border-slate-700 text-white font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Type *</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({...formData, type: v})}
                >
                  <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {PROMOTION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Scope *</Label>
                <Select 
                  value={formData.scope} 
                  onValueChange={(v) => setFormData({...formData, scope: v})}
                >
                  <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {PROMOTION_SCOPES.map(scope => (
                      <SelectItem key={scope.value} value={scope.value}>{scope.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Discount Value */}
            {formData.type === 'PERCENT_DISCOUNT' && (
              <div>
                <Label className="text-slate-300">Percentage Off *</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.percent_off}
                    onChange={(e) => setFormData({...formData, percent_off: e.target.value})}
                    placeholder="50"
                    className="bg-slate-800 border-slate-700 text-white pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                </div>
              </div>
            )}

            {formData.type === 'FIXED_AMOUNT_DISCOUNT' && (
              <div>
                <Label className="text-slate-300">Amount Off (cents) *</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    min="0"
                    value={formData.amount_off_cents}
                    onChange={(e) => setFormData({...formData, amount_off_cents: e.target.value})}
                    placeholder="5000 = $50.00"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>
            )}

            {formData.type === 'FREE_SEATS' && (
              <div>
                <Label className="text-slate-300">Number of Free Seats *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.free_seat_count}
                  onChange={(e) => setFormData({...formData, free_seat_count: e.target.value})}
                  placeholder="5"
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_at}
                  onChange={(e) => setFormData({...formData, start_at: e.target.value})}
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">End Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.end_at}
                  onChange={(e) => setFormData({...formData, end_at: e.target.value})}
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Max Total Redemptions</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.max_redemptions}
                  onChange={(e) => setFormData({...formData, max_redemptions: e.target.value})}
                  placeholder="Unlimited"
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Max Per Tenant</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.max_redemptions_per_tenant}
                  onChange={(e) => setFormData({...formData, max_redemptions_per_tenant: e.target.value})}
                  placeholder="Unlimited"
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Internal notes about this promotion..."
                className="mt-1 bg-slate-800 border-slate-700 text-white"
              />
            </div>

            <div className="flex items-center gap-6 pt-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.applies_to_new_tenants_only}
                  onCheckedChange={(v) => setFormData({...formData, applies_to_new_tenants_only: v})}
                />
                <Label className="text-slate-300">New tenants only</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.stackable}
                  onCheckedChange={(v) => setFormData({...formData, stackable: v})}
                />
                <Label className="text-slate-300">Stackable with others</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleCreate}
              disabled={saving || !formData.name}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Promotion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Promotion Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Promotion</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1 bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Coupon Code</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                className="mt-1 bg-slate-800 border-slate-700 text-white font-mono"
              />
            </div>
            <div>
              <Label className="text-slate-300">End Date</Label>
              <Input
                type="date"
                value={formData.end_at}
                onChange={(e) => setFormData({...formData, end_at: e.target.value})}
                className="mt-1 bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="mt-1 bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleUpdate}
              disabled={saving}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Promotion Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              Assign Promotion to Tenant
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedPromotion?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Select Tenant *</Label>
              <Select 
                value={assignData.tenant_id} 
                onValueChange={(v) => setAssignData({...assignData, tenant_id: v})}
              >
                <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Choose a tenant..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-[200px]">
                  {tenants.map(tenant => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPromotion?.scope === 'TENANT_SPECIFIC_SEATS' && (
              <div>
                <Label className="text-slate-300">Number of Seats to Comp</Label>
                <Input
                  type="number"
                  min="0"
                  value={assignData.seat_count_comped}
                  onChange={(e) => setAssignData({...assignData, seat_count_comped: e.target.value})}
                  placeholder="Number of seats"
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            )}

            <div>
              <Label className="text-slate-300">Override End Date (optional)</Label>
              <Input
                type="date"
                value={assignData.effective_end_at}
                onChange={(e) => setAssignData({...assignData, effective_end_at: e.target.value})}
                className="mt-1 bg-slate-800 border-slate-700 text-white"
              />
            </div>

            <div>
              <Label className="text-slate-300">Notes</Label>
              <Textarea
                value={assignData.notes}
                onChange={(e) => setAssignData({...assignData, notes: e.target.value})}
                placeholder="Internal notes..."
                className="mt-1 bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleAssign}
              disabled={saving || !assignData.tenant_id}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign Promotion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promotion Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {promotionDetails?.promotion?.name}
              <Badge className={STATUS_COLORS[promotionDetails?.promotion?.status]}>
                {promotionDetails?.promotion?.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {promotionDetails && (
            <div className="space-y-6 py-4">
              {/* Promotion Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Type:</span>
                  <span className="ml-2 text-white">
                    {PROMOTION_TYPES.find(t => t.value === promotionDetails.promotion.type)?.label}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">Scope:</span>
                  <span className="ml-2 text-white">
                    {PROMOTION_SCOPES.find(s => s.value === promotionDetails.promotion.scope)?.label}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">Discount:</span>
                  <span className="ml-2 text-white">{formatDiscount(promotionDetails.promotion)}</span>
                </div>
                <div>
                  <span className="text-slate-400">Redemptions:</span>
                  <span className="ml-2 text-white">{promotionDetails.promotion.total_redemptions || 0}</span>
                </div>
                {promotionDetails.promotion.code && (
                  <div>
                    <span className="text-slate-400">Code:</span>
                    <Badge className="ml-2 bg-slate-700 text-white font-mono">
                      {promotionDetails.promotion.code}
                    </Badge>
                  </div>
                )}
                {promotionDetails.promotion.end_at && (
                  <div>
                    <span className="text-slate-400">Ends:</span>
                    <span className="ml-2 text-white">
                      {new Date(promotionDetails.promotion.end_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Assignments */}
              <div>
                <h4 className="font-medium text-white mb-3">Tenant Assignments</h4>
                {promotionDetails.assignments?.length === 0 ? (
                  <p className="text-slate-400 text-sm">No tenants assigned yet</p>
                ) : (
                  <div className="space-y-2">
                    {promotionDetails.assignments?.map((assignment) => (
                      <div 
                        key={assignment.id} 
                        className={`p-3 rounded-lg border ${
                          assignment.is_revoked 
                            ? 'bg-slate-800/50 border-slate-700 opacity-60' 
                            : 'bg-slate-800 border-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-white">
                              {assignment.tenant_name}
                            </span>
                            {assignment.is_revoked && (
                              <Badge className="ml-2 bg-red-500/20 text-red-400">Revoked</Badge>
                            )}
                            {assignment.seat_count_comped && (
                              <span className="ml-2 text-sm text-slate-400">
                                ({assignment.seat_count_comped} seats comped)
                              </span>
                            )}
                          </div>
                          {!assignment.is_revoked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleRevokeAssignment(assignment.id)}
                            >
                              Revoke
                            </Button>
                          )}
                        </div>
                        {assignment.notes && (
                          <p className="text-sm text-slate-500 mt-1">{assignment.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
